import React, { useState, useEffect } from "react";
import Breadcrums from "../../common/breadcrumbs";
import Loader from "../../common/loader";
import CustomPagination from "../../common/custompagination";
import {
    globalLoader,
    handleServerValidations,
    formateDateWithMonth,
} from "../../../../utils/commonfunction";
import Swal from "sweetalert2";
import * as withdrawalServices from "../../services/withdrawalServices";
import { useTranslation } from "react-i18next";

const ManualWithdrawalList = () => {
    const { t } = useTranslation();
    const [list, setList] = useState([]);
    const [datalength, setDataLength] = useState(0);
    const [loader, setLoader] = useState(true);
    const [statsupdate, setStatsUpdate] = useState(Date.now());

    // Pagination & Search States (Server-side)
    const [page, setPage] = useState(1);
    const [itemperpage] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");

    const breadcrumbs = [
        { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
        { title: "Manual Transfer", url: "" },
    ];

    // Fetch list (Server-side)
    useEffect(() => {
        const timer = setTimeout(() => {
            setLoader(true);
            const params = {
                page: page,
                per_page: itemperpage,
                global_search: searchTerm
            };

            withdrawalServices
                .ListAstrologers(params)
                .then((response) => {
                    if (response?.success) {
                        // Backend now returns data.list and data.total_records
                        setList(response.data.list || []);
                        setDataLength(response.data.total_records || 0);
                    }
                    setLoader(false);
                })
                .catch((err) => {
                    console.log("Error fetching astrologers:", err);
                    setLoader(false);
                });
        }, 400); // Debounce search

        return () => clearTimeout(timer);
    }, [page, searchTerm, statsupdate]);

    // View History Modal
    const handleViewHistory = (astrologerId, astrologerName) => {
        globalLoader(true);
        withdrawalServices
            .History({})
            .then((res) => {
                globalLoader(false);
                if (res.success) {
                    const historyData = res.data.history || res.data.list || [];
                    const filteredHistory = historyData.filter(
                        (h) => h.astrologer == astrologerId || h.astrologer_details?.id == astrologerId
                    );

                    if (filteredHistory.length === 0) {
                        Swal.fire("No History", `No transfer history found for ${astrologerName}`, "info");
                        return;
                    }

                    const historyHtml = `
            <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
              <table class="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  ${filteredHistory
                            .map(
                                (h) => {
                                    const currency = h.astrologer_details?.astrologer_wallet?.currency_type === 'USD' ? '$' : '₹';
                                    return `
                    <tr>
                      <td>${formateDateWithMonth(h.createdAt)}</td>
                      <td>${currency}${parseFloat(h.amount).toLocaleString(currency === '$' ? "en-US" : "en-IN")}</td>
                      <td><span class="badge bg-secondary">${h.method.toUpperCase()}</span></td>
                      <td style="font-size: 0.8rem;">${h.remarks || "-"}</td>
                    </tr>
                  `
                                }
                            )
                            .join("")}
                </tbody>
              </table>
            </div>
          `;

                    Swal.fire({
                        title: `Transfer History: ${astrologerName}`,
                        html: historyHtml,
                        width: "800px",
                        confirmButtonText: "Close",
                    });
                }
            })
            .catch(() => {
                globalLoader(false);
                Swal.fire("Error", "Failed to fetch history", "error");
            });
    };

    // Manual Withdrawal Modal
    const handleWithdrawal = (astrologer) => {
        const balance = astrologer.astrologer_wallet?.balance || 0;
        const currency = astrologer.astrologer_wallet?.currency_type === 'USD' ? '$' : '₹';

        Swal.fire({
            title: `Manual Transfer for ${astrologer.name}`,
            html: `
        <div class="text-start mt-3">
          <p><strong>Current Wallet Balance:</strong> ${currency}${parseFloat(balance).toLocaleString(currency === '$' ? "en-US" : "en-IN")}</p>
          <div class="form-group mb-3">
            <label class="form-label">Transfer Amount (${currency})</label>
            <input type="number" id="withdrawAmount" class="form-control" placeholder="Enter amount" min="1" max="${balance}">
          </div>
          <div class="form-group mb-3">
            <label class="form-label">Method</label>
            <select id="withdrawMethod" class="form-control">
              <option value="bank">Bank Transfer</option>
              <option value="upi">UPI</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Remarks</label>
            <textarea id="withdrawRemarks" class="form-control" rows="2" placeholder="Administrative transfer..."></textarea>
          </div>
        </div>
      `,
            showCancelButton: true,
            confirmButtonText: "Process Transfer",
            preConfirm: () => {
                const amount = document.getElementById("withdrawAmount").value;
                const method = document.getElementById("withdrawMethod").value;
                const remarks = document.getElementById("withdrawRemarks").value;

                if (!amount || parseFloat(amount) <= 0) {
                    Swal.showValidationMessage("Please enter a valid amount");
                    return false;
                }
                if (parseFloat(amount) > parseFloat(balance)) {
                    Swal.showValidationMessage("Amount cannot exceed available balance");
                    return false;
                }
                if (!method) {
                    Swal.showValidationMessage("Please select a method");
                    return false;
                }

                return {
                    astrologer_id: astrologer.id,
                    amount: parseFloat(amount),
                    method: method,
                    remarks: remarks
                };
            },
        }).then((result) => {
            if (result.isConfirmed) {
                globalLoader(true);
                withdrawalServices
                    .ManualWithdrawal(result.value)
                    .then((res) => {
                        globalLoader(false);
                        if (res.success) {
                            Swal.fire("Success!", res.message, "success");
                            setStatsUpdate(Date.now());
                        } else {
                            Swal.fire("Error", handleServerValidations(res), "error");
                        }
                    })
                    .catch(() => {
                        globalLoader(false);
                        Swal.fire("Error", "Failed to process transfer", "error");
                    });
            }
        });
    };

    return (
        <>
            <Breadcrums data={breadcrumbs} />

            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h6 className="main-content-label mb-0">Transfer Management</h6>

                            <div className="d-flex align-items-center gap-2">
                                <div className="form-group mb-0">
                                    <div className="form-group mb-0 rangepicker_container filter_design">
                                        <i className="fa fa-search calender_icon"></i>
                                        <input
                                            type="search"
                                            className="form-control"
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                setPage(1);
                                            }}
                                            placeholder="Search Astrologer..."
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-bordered text-nowrap w-100">
                                <thead>
                                    <tr>
                                        <th>S.No</th>
                                        <th>Astrologer</th>
                                        <th>Wallet Balance</th>
                                        <th className="action_head">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loader ? (
                                        <tr>
                                            <td colSpan="4" className="text-center">
                                                <Loader />
                                            </td>
                                        </tr>
                                    ) : list.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="text-center">
                                                No astrologers found
                                            </td>
                                        </tr>
                                    ) : (
                                        list.map((row, i) => (
                                            <tr key={row.id}>
                                                <td>{(page - 1) * itemperpage + i + 1}</td>
                                                <td>
                                                    <strong>{row.name}</strong>
                                                    <br />
                                                    <small className="text-muted">{row.email || row.phone_number}</small>
                                                </td>
                                                <td>
                                                    <b className="text-success">
                                                        {row.astrologer_wallet?.currency_type === 'USD' ? '$' : '₹'}{parseFloat(row.astrologer_wallet?.balance || 0).toLocaleString(row.astrologer_wallet?.currency_type === 'USD' ? "en-US" : "en-IN")}
                                                    </b>
                                                </td>
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-success btn-sm me-2"
                                                        onClick={() => handleWithdrawal(row)}
                                                        style={{ backgroundColor: "#28a745", color: "#fff", border: "none" }}
                                                    >
                                                        Transfer
                                                    </button>
                                                    <button
                                                        className="btn btn-primary btn-sm"
                                                        onClick={() => handleViewHistory(row.id, row.name)}
                                                        style={{ backgroundColor: "#007bff", color: "#fff", border: "none" }}
                                                    >
                                                        View History
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {datalength > itemperpage && (
                            <div className="mt-3">
                                <CustomPagination
                                    datalength={datalength}
                                    itemperpage={itemperpage}
                                    setPage={setPage}
                                    currentPage={page}
                                    pageRoute={[{ name: "Manual Transfer", path: "/admin/withdrawal/manual-list" }]}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ManualWithdrawalList;
